import { omit, get, mergeWith } from 'lodash-es'

// state
const createSingleModelRuntimeState = () => ({
  currentAbortController: null,
  loading: false,
})

const createProgressModelRuntimeState = () => ({
  ...createSingleModelRuntimeState(),
  progress: null,
})

const createOperaRuntimeState = () => ({
  ...createProgressModelRuntimeState(),
  progressLabel: null,
})

const createBasePersistedState = () => ({
  metadata: null,
  results: {},
})

const createProgressModelState = () => ({
  ...createBasePersistedState(),
  ...createProgressModelRuntimeState(),
})

const createEpiSuiteState = () => ({
  ...createProgressModelState(),
  results: [],
})

const createOperaState = () => ({
  ...createBasePersistedState(),
  ...createOperaRuntimeState(),
})

const createEndpointModelState = () => ({
  ...createBasePersistedState(),
  ...createSingleModelRuntimeState(),
})

const createWebTestState = () => ({
  ...createEndpointModelState(),
})

const createGroupedModelRuntimeState = () => ({
  currentAbortControllerByModel: {},
  loadingByModel: {},
})

const createGroupedModelState = () => ({
  metadata: null,
  resultsByModel: {},
  ...createGroupedModelRuntimeState(),
})

// getters
const createSingleModelGetters = () => ({
  getResults: (state) => state.results,
  getIsLoading: (state) => state.loading,
  getIsAborted: (state) => !!state?.currentAbortController?.signal?.aborted,
})

const createProgressModelGetters = () => ({
  ...createSingleModelGetters(),
  getMetadata: (state) => state.metadata,
  getProgress: (state) => state.progress,
})

const createEpiSuiteGetters = () => ({
  ...createProgressModelGetters(),
})

const createEndpointModelGetters = () => ({
  ...createSingleModelGetters(),
  getEndpoints: (state) => state.metadata?.endpoints,
  getVersion: (state) => state.metadata?.version,
})

const createWebTestGetters = () => ({
  ...createEndpointModelGetters(),
})

const createOperaGetters = () => ({
  ...createProgressModelGetters(),
  getProgressLabel: (state) => state.progressLabel,
  getEndpoints: (state) => state.metadata?.endpoints,
  getVersion: (state) => state.metadata?.version,
})

const createGroupedModelGetters = () => ({
  getMetadata: (state) => state.metadata,
  getIsLoadingForModel: (state) => (modelId) => get(state.loadingByModel, modelId, false),
  getResultsForModel: (state) => (modelId) => get(state.resultsByModel, modelId, []),
  getIsAbortedForModel: (state) => (modelId) =>
    !!get(state?.currentAbortControllerByModel, `${modelId}.signal.aborted`, false),
})

// mutations
const createBasicMutations = () => ({
  SET_METADATA: (state, payload) => {
    state.metadata = payload
  },
})

const createSingleModelMutations = () => ({
  ...createBasicMutations(),

  SET_LOADING: (state, loading) => {
    state.loading = loading
  },
  SET_ABORT_CONTROLLER: (state, controller) => {
    state.currentAbortController = controller
  },

  ABORT_CURRENT_FETCH: (state) => {
    if (state.currentAbortController) {
      state.currentAbortController.abort()
    }
  },
})

const createProgressModelMutations = () => ({
  ...createSingleModelMutations(),
  SET_PROGRESS: (state, progress) => {
    state.progress = progress
  },
  RESET_STATE: (state) => {
    state.results = {}
    state.progress = null
    state.currentAbortController = null
    state.loading = false
  },
})

const createEpiSuiteMutations = () => ({
  ...createProgressModelMutations(),
  PUSH_RESULT: (state, payload) => {
    state.results.push(payload)
  },
  FILTER_RESULTS_BY_SMILES_SET: (state, smilesSet) => {
    state.results = state.results.filter((result) => smilesSet.has(result?.chemical?.smiles))
  },
  RESET_STATE: (state) => {
    state.results = []
    state.progress = null
    state.currentAbortController = null
    state.loading = false
  },
})

const createEndpointModelMutations = () => ({
  ...createSingleModelMutations(),
  PUSH_RESULTS: (state, payload) => {
    if (!payload) return
    state.results = mergeWith({}, state.results, payload, (objVal, srcVal) =>
      Array.isArray(objVal) && Array.isArray(srcVal) ? objVal.concat(srcVal) : undefined,
    )
  },
})

const createWebTestMutations = () => ({
  ...createEndpointModelMutations(),
  RESET_STATE: (state) => {
    state.results = {}
    state.currentAbortController = null
    state.loading = false
  },
})

const createOperaMutations = () => ({
  ...createEndpointModelMutations(),
  ...createProgressModelMutations(),

  SET_PROGRESS_LABEL: (state, label) => {
    state.progressLabel = label
  },

  RESET_STATE: (state) => {
    state.results = {}
    state.progress = null
    state.progressLabel = null
    state.currentAbortController = null
    state.loading = false
  },
})

const createGroupedModelMutations = () => ({
  ...createBasicMutations(),

  PUSH_RESULTS_FOR_MODEL: (state, payload) => {
    if (!payload) return
    const { modelId, results } = payload || {}

    if (!modelId) return
    const existing = state.resultsByModel?.[modelId] || {}
    state.resultsByModel = {
      ...state.resultsByModel,
      [modelId]: mergeWith({}, existing, results, (objVal, srcVal) =>
        Array.isArray(objVal) && Array.isArray(srcVal) ? objVal.concat(srcVal) : undefined,
      ),
    }
  },

  SET_LOADING_FOR_MODEL: (state, payload) => {
    const { modelId, loading } = payload || {}
    if (!modelId) return
    state.loadingByModel = { ...state.loadingByModel, [modelId]: loading }
  },

  SET_ABORT_CONTROLLER_FOR_MODEL: (state, payload) => {
    const { modelId, controller } = payload || {}
    if (!modelId) return
    state.currentAbortControllerByModel = { ...state.currentAbortControllerByModel, [modelId]: controller }
  },

  RESET_STATE_FOR_MODEL: (state, modelId) => {
    if (!modelId) return
    state.resultsByModel = omit(state.resultsByModel, modelId)
    state.currentAbortControllerByModel = omit(state.currentAbortControllerByModel, modelId)
    state.loadingByModel = { ...state.loadingByModel, [modelId]: false }
  },

  ABORT_CURRENT_FETCH_FOR_MODEL: (state, payload) => {
    const modelId = payload?.modelId
    if (!modelId) return
    const controller = state.currentAbortControllerByModel[modelId]
    if (controller) {
      controller.abort()
    }
  },
})

const singleModelBaseKeys = {
  getResults: 'getResults',
  getIsLoading: 'getIsLoading',
  getIsAborted: 'getIsAborted',
  SET_METADATA: 'SET_METADATA',
  SET_LOADING: 'SET_LOADING',
  SET_ABORT_CONTROLLER: 'SET_ABORT_CONTROLLER',
  ABORT_CURRENT_FETCH: 'ABORT_CURRENT_FETCH',
  RESET_STATE: 'RESET_STATE',
}

const endpointModelKeys = {
  ...singleModelBaseKeys,
  getEndpoints: 'getEndpoints',
  getVersion: 'getVersion',
  PUSH_RESULTS: 'PUSH_RESULTS',
}

const progressModelKeys = {
  ...singleModelBaseKeys,
  getMetadata: 'getMetadata',
  getProgress: 'getProgress',
  SET_PROGRESS: 'SET_PROGRESS',
}

const epiSuiteKeys = {
  ...progressModelKeys,
  PUSH_RESULT: 'PUSH_RESULT',
  FILTER_RESULTS_BY_SMILES_SET: 'FILTER_RESULTS_BY_SMILES_SET',
}

const operaKeys = {
  ...progressModelKeys,
  ...endpointModelKeys,
  getProgressLabel: 'getProgressLabel',
  SET_PROGRESS_LABEL: 'SET_PROGRESS_LABEL',
  getEndpoints: 'getEndpoints',
  getVersion: 'getVersion',
}

const webTestKeys = {
  ...endpointModelKeys,
}

const groupedModelKeys = {
  getMetadata: 'getMetadata',
  getIsLoadingForModel: 'getIsLoadingForModel',
  getResultsForModel: 'getResultsForModel',
  getIsAbortedForModel: 'getIsAbortedForModel',
  SET_METADATA: 'SET_METADATA',
  PUSH_RESULTS_FOR_MODEL: 'PUSH_RESULTS_FOR_MODEL',
  SET_LOADING_FOR_MODEL: 'SET_LOADING_FOR_MODEL',
  SET_ABORT_CONTROLLER_FOR_MODEL: 'SET_ABORT_CONTROLLER_FOR_MODEL',
  RESET_STATE_FOR_MODEL: 'RESET_STATE_FOR_MODEL',
  ABORT_CURRENT_FETCH_FOR_MODEL: 'ABORT_CURRENT_FETCH_FOR_MODEL',
}

const modelStateRegistry = {
  epiSuite: {
    createState: createEpiSuiteState,
    createGetters: createEpiSuiteGetters,
    createMutations: createEpiSuiteMutations,
    keys: epiSuiteKeys,
  },
  webtest: {
    createState: createWebTestState,
    createGetters: createWebTestGetters,
    createMutations: createWebTestMutations,
    keys: webTestKeys,
  },
  opera: {
    createState: createOperaState,
    createGetters: createOperaGetters,
    createMutations: createOperaMutations,
    keys: operaKeys,
  },
  groupedModel: {
    createState: createGroupedModelState,
    createGetters: createGroupedModelGetters,
    createMutations: createGroupedModelMutations,
    keys: groupedModelKeys,
  },
}

export {
  createGroupedModelRuntimeState,
  createProgressModelRuntimeState,
  createSingleModelRuntimeState,
  createOperaRuntimeState,
  modelStateRegistry,
}
